/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Floating "go to the comparison" call-to-action for long content pages.
 * Deep in a kennisbank article, the about page or the provider list there is
 * no comparer in sight; this keeps a one-click route back to it on screen.
 *
 * Two renderings of the same thing, split at Tailwind's `lg` — the breakpoint
 * where the site header itself switches between hamburger and desktop nav:
 *   - desktop (lg+): a small card bottom-right — title, one-line reason, an
 *     emerald button, a dismiss × — the sticky-CTA pattern that stays out of
 *     the reading column;
 *   - mobile (<lg): a centred pill, styled like the comparer's shortlist tray
 *     (comparison/ShortlistTray.tsx) and the article share pill (same
 *     backdrop, border, radius, 40px hit targets) so the three floating bottom
 *     affordances read as one family.
 *
 * When it shows: route allows it (lib/floatingCompareCta.ts), the page is long
 * (hooks/useIsLongPage.ts — the same gate as the scroll-progress bar), the
 * visitor has scrolled about a screen down, no part of the footer is in view,
 * the cookie banner is not occupying the bottom edge, and it has not been
 * dismissed this session. The rules themselves live in lib/floatingCompareCta.ts
 * (isFloatingCtaVisible); this component only feeds them. Dismissal is per
 * browser session on purpose: a sticky CTA that returns on the very next page
 * after you closed it reads as nagging, one that returns next visit reads as
 * normal. The footer gate is not a dismissal: the CTA steps aside while the
 * footer is in view (its legal row sits right under the desktop card) and
 * comes back when the footer leaves.
 *
 * SSR: every gate starts false (the footer gate "not in view", which cannot
 * show anything on its own), so prerendered HTML and the first client render
 * agree (nothing rendered) and the element only ever mounts client-side. The
 * scroll gate is rAF-coalesced and reads the scroll position once per frame;
 * the footer gate is an IntersectionObserver, which also catches the footer
 * moving into view without a scroll (a block above it collapsing). Under
 * `prefers-reduced-motion` the card fades without sliding (MotionConfig).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, MotionConfig, m } from 'motion/react';
import { ArrowRight, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCookies } from '../contexts/CookieContext';
import { useIsLongPage } from '../hooks/useIsLongPage';
import { DESKTOP_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import {
  floatingCtaHref,
  footerInViewFromEntries,
  footerRectInView,
  isFloatingCtaEligible,
  isFloatingCtaVisible,
  nextScrolledIn,
  type FloatingCtaVariant,
} from '../lib/floatingCompareCta';
import { SURFACES, pageCategoryForPath, splitLanguageFromPath, trackEvent } from '../lib/gtag';
import { LogoMark } from './LogoMark';

/** sessionStorage flag — one dismissal silences the CTA for the rest of the session. */
const DISMISS_KEY = 'floating_compare_cta_dismissed';
/** The site footer's id (Footer.tsx); the CTA steps aside while any of it is in view. */
const FOOTER_ID = 'site-footer';

const readDismissed = (): boolean => {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false; // storage blocked (private mode / policy): behave as never dismissed
  }
};

const writeDismissed = (): void => {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, '1');
  } catch {
    // storage blocked: the in-memory state still hides it for this page view
  }
};

export const FloatingCompareCta: React.FC = () => {
  const { language, t } = useLanguage();
  const { hasConsent } = useCookies();
  const { pathname } = useLocation();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const variant: FloatingCtaVariant = isDesktop ? 'desktop' : 'mobile';

  const eligible = isFloatingCtaEligible(pathname, variant);
  const isLong = useIsLongPage(eligible);
  const [dismissed, setDismissed] = useState(false);
  const [scrolledIn, setScrolledIn] = useState(false);
  const [footerInView, setFooterInView] = useState(false);

  useEffect(() => {
    setDismissed(readDismissed());
  }, []);

  // Scroll gate: past the show mark (nextScrolledIn, with its hysteresis).
  useEffect(() => {
    if (!eligible || !isLong || dismissed) {
      setScrolledIn(false);
      return;
    }
    let frame = 0;
    const measure = () => {
      frame = 0;
      const vh = window.innerHeight;
      const y = window.scrollY;
      setScrolledIn((was) => nextScrolledIn(was, y, vh));
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
    // pathname: <ScrollToTop> resets the scroll on navigation, but that fires no
    // scroll event when the position was already 0 — re-measure explicitly.
  }, [eligible, isLong, dismissed, pathname]);

  // Footer gate: any pixel of the footer in the viewport hides the CTA, and it
  // returns when the footer leaves. An observer, not a rect read in the scroll
  // handler: it also fires when the footer moves into view without a scroll.
  // The footer lives in the layout (App.tsx) and outlives every route, so one
  // observer per eligible stretch suffices. Its first record arrives a frame
  // after observe(), so the gate is seeded from one rect read in the same
  // flush as the scroll gate's first measure (footerRectInView) — a gate that
  // opens with the footer already on screen never mounts the card for a
  // frame. No observer (jsdom, never a browser this bundle targets): the gate
  // stays open, as without a footer.
  useEffect(() => {
    if (!eligible || dismissed) return;
    const footer = document.getElementById(FOOTER_ID);
    if (!footer || typeof IntersectionObserver === 'undefined') return;
    setFooterInView(footerRectInView(footer.getBoundingClientRect(), window.innerHeight));
    const observer = new IntersectionObserver((entries) => {
      setFooterInView((was) => footerInViewFromEntries(entries, was));
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }, [eligible, dismissed]);

  const pageCategory = pageCategoryForPath(splitLanguageFromPath(pathname).path) ?? 'other';
  const href = floatingCtaHref(pathname, language);

  const onCtaClick = useCallback(() => {
    trackEvent('floating_cta_click', {
      surface: SURFACES.floatingCta,
      variant,
      page_category: pageCategory,
      language,
    });
    // No scroll hand-off needed: the CTA never shows on the homepage (route
    // gate), so the click is always a route change, and <ScrollToTop> lands
    // the visitor where the comparison starts — Stap 1 at the top from `sm`,
    // the results on a phone; see getComparerHref.
  }, [variant, pageCategory, language]);

  const onDismiss = useCallback(() => {
    trackEvent('floating_cta_dismiss', {
      surface: SURFACES.floatingCta,
      variant,
      page_category: pageCategory,
      language,
    });
    writeDismissed();
    setDismissed(true);
  }, [variant, pageCategory, language]);

  // hasConsent: the cookie banner owns the bottom edge (z-100, full width)
  // until a choice is recorded; the CTA would only peek out from behind it.
  const visible = isFloatingCtaVisible({
    eligible,
    isLong,
    scrolledIn,
    footerInView,
    dismissed,
    hasConsent,
  });

  // reducedMotion="user": under prefers-reduced-motion the enter/exit keeps
  // its fade and drops the slide and the scale (the ComparisonEngine idiom).
  return (
    <MotionConfig reducedMotion="user">
      <AnimatePresence>
        {visible && isDesktop && (
          <m.div
            key="floating-cta-desktop"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 right-6 z-40 w-[19rem] max-w-[calc(100vw-3rem)]"
          >
            <div className="relative rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-slate-700/60 shadow-2xl shadow-slate-950/60 p-4">
              <div className="flex items-start gap-3 pr-7">
                <span
                  aria-hidden="true"
                  className="shrink-0 w-9 h-9 rounded-xl bg-emerald-900/30 flex items-center justify-center"
                >
                  <LogoMark className="w-5 h-5 text-emerald-400" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-black text-white leading-snug">
                    {t('floating_cta.title')}
                  </p>
                  <p className="mt-0.5 text-xs font-medium text-fg-muted leading-snug">
                    {t('floating_cta.subtitle')}
                  </p>
                </div>
              </div>
              <Link
                to={href}
                onClick={onCtaClick}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 px-4 py-2.5 text-sm font-black text-white shadow-lg shadow-emerald-900/20 transition-all focus-ring"
              >
                {t('floating_cta.cta')}
                <ArrowRight aria-hidden="true" className="w-4 h-4" />
              </Link>
              <button
                type="button"
                onClick={onDismiss}
                aria-label={t('floating_cta.dismiss')}
                title={t('floating_cta.dismiss')}
                className="absolute top-2 right-2 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all focus-ring"
              >
                <X aria-hidden="true" className="w-4 h-4" />
              </button>
            </div>
          </m.div>
        )}

        {visible && !isDesktop && (
          <m.div
            key="floating-cta-mobile"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-40 flex justify-center px-3 pointer-events-none"
          >
            {/* No backdrop-filter on the phone pill: a blur under a fixed
              element is re-sampled on every scrolled frame beneath it. */}
            <div className="pointer-events-auto flex items-center gap-0.5 bg-slate-900/95 border border-slate-700/60 rounded-full shadow-2xl shadow-slate-950/60 p-1">
              <Link
                to={href}
                onClick={onCtaClick}
                className="flex items-center gap-2 h-10 pl-4 pr-3 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-black transition-all focus-ring"
              >
                {t('floating_cta.cta')}
                <ArrowRight aria-hidden="true" className="w-4 h-4" />
              </Link>
              <button
                type="button"
                onClick={onDismiss}
                aria-label={t('floating_cta.dismiss')}
                title={t('floating_cta.dismiss')}
                className="w-10 h-10 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-all focus-ring"
              >
                <X aria-hidden="true" className="w-4 h-4" />
              </button>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
};
